/**
 * A middleware that does nothing. It allows args to pass through unmodified.
 * Just used to demonstrate middleware
 * @type {import('./types').MqttMiddleware<{[x: string]: any}>}
 */
function dummyMiddleware(next) {
	return args => next(args);
}

/**
 * A middleware that parses incoming message as JSON string.
 * The resulting javascript object is passed on to the next `{@link MessageHandlerFunc}` as the message.
 * @type {import('./types').MqttMiddleware<{[x: string]: any}>}
 */
function jsonMessageMiddleware(next) {
	return args => {
		const { message } = args;
		const messageParsedAsJSON = JSON.parse(message);
		next({ ...args, message: messageParsedAsJSON });
	};
}

/**
 * Build a middleware that passes on `dependencies` to the handler.
 *
 * It is recommended to not have dependencies with the same names as
 * 	message, params, matchedTopic, originalTopic or mqttClient
 * @param  {D} dependencies
 * @returns {import('./types').MqttMiddleware<D>}
 * @template D
 */
function dependencyInjectionMiddlewareBuilder(dependencies) {
	// this returned function, is the actual middleware
	return next => {
		// this returned function will become the 'next' argument for any other middleware
		//   down the chain
		return args => {
			// inject the dependencies into the next middleware, as args
			next({ ...args, ...dependencies });
		};
	};
}

/**
 * Build a middleware that catches any errors in subsequent handlers and calls `errorHandler`.
 * @param {(args: import('./types').MessageHandlerFuncArgs<{error: Error}>) => void} errorCallback
 * @returns {import('./types').MqttMiddleware<{[x:string]: any}>}
 */
function errorHandlingMiddlewareBuilder(errorCallback) {
	return next => {
		return args => {
			try {
				next(args);
			} catch (error) {
				errorCallback({ ...args, error });
			}
		};
	};
}

module.exports = {
	dummyMiddleware,
	jsonMessageMiddleware,
	dependencyInjectionMiddlewareBuilder,
	errorHandlingMiddlewareBuilder,
};
